import React from "react";
import { FormattedMessage, FormattedDate, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Pane, KeyValue, Row, Col, Loading } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchAuditLogDetail } from "../api/auditLogService";
// CZNA-1609: a permanently deleted user has no record left to open — a link would lead nowhere.
var isPurgedUser = function (actionType, entityType) {
    return actionType === "USER_PURGED" && entityType === "USER";
};
/** "Last, First" of the deleted user, kept in the entry's old values as of the deletion. */
var purgedUserName = function (oldValue) {
    var lastName = oldValue === null || oldValue === void 0 ? void 0 : oldValue.last_name;
    var firstName = oldValue === null || oldValue === void 0 ? void 0 : oldValue.first_name;
    if (lastName == null && firstName == null)
        return null;
    return [lastName, firstName].filter(function (part) { return part != null; }).join(", ");
};
var getEntityLink = function (actionType, entityType, entityId) {
    if (isPurgedUser(actionType, entityType))
        return null;
    if (entityType === "USER")
        return "/czna-users/users/".concat(entityId);
    if (entityType === "ORGANIZATION")
        return "/czna-users/organizations/".concat(entityId);
    return null;
};
var AuditLogDetailPane = function (_a) {
    var id = _a.id, onClose = _a.onClose;
    var intl = useIntl();
    var ky = useOkapiKy();
    var _b = useQuery(["czna-users", "audit-log-detail", id], function () { return fetchAuditLogDetail(ky, id); }, { staleTime: Infinity }), detail = _b.data, isLoading = _b.isLoading;
    var renderChangesTable = function () {
        if (!(detail === null || detail === void 0 ? void 0 : detail.changes))
            return null;
        var fields = detail.changes.fields || [];
        if (fields.length === 0)
            return null;
        return (React.createElement("div", { style: { marginTop: "1rem" } },
            React.createElement("strong", null,
                React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.field" })),
            React.createElement("table", { style: {
                    width: "100%",
                    marginTop: "0.5rem",
                    borderCollapse: "collapse",
                } },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", { style: {
                                textAlign: "left",
                                padding: "4px 8px",
                                borderBottom: "1px solid #ccc",
                            } },
                            React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.field" })),
                        React.createElement("th", { style: {
                                textAlign: "left",
                                padding: "4px 8px",
                                borderBottom: "1px solid #ccc",
                            } },
                            React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.oldValue" })),
                        React.createElement("th", { style: {
                                textAlign: "left",
                                padding: "4px 8px",
                                borderBottom: "1px solid #ccc",
                            } },
                            React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.newValue" })))),
                React.createElement("tbody", null, fields.map(function (field) {
                    var _a, _b;
                    return (React.createElement("tr", { key: field },
                        React.createElement("td", { style: { padding: "4px 8px" } }, field),
                        React.createElement("td", { style: { padding: "4px 8px" } }, ((_a = detail.oldValue) === null || _a === void 0 ? void 0 : _a[field]) != null
                            ? String(detail.oldValue[field])
                            : "-"),
                        React.createElement("td", { style: { padding: "4px 8px" } }, ((_b = detail.newValue) === null || _b === void 0 ? void 0 : _b[field]) != null
                            ? String(detail.newValue[field])
                            : "-")));
                })))));
    };
    var renderContent = function () {
        if (isLoading)
            return React.createElement(Loading, null);
        if (!detail)
            return React.createElement(FormattedMessage, { id: "ui-czna-users.error.notFound" });
        var entityLink = getEntityLink(detail.actionType, detail.entityType, detail.entityId);
        var entityTypeLabel = intl.formatMessage({
            id: "ui-czna-users.auditLog.entityType.".concat(detail.entityType),
            defaultMessage: detail.entityType,
        });
        var purged = isPurgedUser(detail.actionType, detail.entityType);
        var deletedUserName = purged ? purgedUserName(detail.oldValue) : null;
        // An admin who purged their own account is gone too.
        var authorIsPurged = purged && detail.userId === detail.entityId;
        return (React.createElement(React.Fragment, null,
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.createdAt" }), value: React.createElement(FormattedDate, { value: detail.createdAt, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.actionType" }), value: intl.formatMessage({
                            id: "ui-czna-users.auditLog.actionType.".concat(detail.actionType),
                            defaultMessage: detail.actionType,
                        }) }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.userName" }), value: authorIsPurged ? (detail.userName) : (React.createElement(Link, { to: "/czna-users/users/".concat(detail.userId) }, detail.userName)) })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.entity" }), value: entityLink ? (React.createElement(Link, { to: entityLink }, entityTypeLabel)) : deletedUserName ? ("".concat(entityTypeLabel, " (").concat(deletedUserName, ")")) : (entityTypeLabel) }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.ipAddress" }), value: detail.ipAddress })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.userAgent" }), value: detail.userAgent || "-" }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.summary" }), value: detail.summary })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.status" }), value: intl.formatMessage({
                            id: "ui-czna-users.auditLog.status.".concat(detail.status),
                            defaultMessage: detail.status,
                        }) }))),
            detail.errorMessage && (React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.errorMessage" }), value: detail.errorMessage })))),
            renderChangesTable()));
    };
    return (React.createElement(Pane, { defaultWidth: "44%", paneTitle: intl.formatMessage({
            id: "ui-czna-users.auditLog.detail.title",
        }), onClose: onClose, dismissible: true }, renderContent()));
};
export default AuditLogDetailPane;
